import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/auth-store'
import { useContentStore } from '@/stores/content-store'
import { EnhancedEditor } from '@/components/publish/enhanced-editor'
import { apiAdapter } from '@/lib/api-adapter'

export default function PublishPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { createPost, isLoading } = useContentStore()

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">请先登录后再发布内容</p>
        <Button onClick={() => navigate('/login')}>
          去登录
        </Button>
      </div>
    )
  }

  const handleSave = (content: string, images: string[], metadata: any) => {
    // 保存草稿逻辑
    toast.success('草稿已保存')
  }

  // 将编辑器里的 base64 图片上传到 /upload/multiple，返回可访问的 URL 列表
  const uploadImages = async (images: string[]): Promise<string[]> => {
    if (images.length === 0) return []

    const files: File[] = []
    for (const dataUrl of images) {
      const blob = await (await fetch(dataUrl)).blob()
      files.push(new File(
        [blob],
        `image_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.png`,
        { type: blob.type || 'image/png' }
      ))
    }

    const uploadRes = await apiAdapter.upload.uploadMultiple(files)
    if (!uploadRes.success || !uploadRes.data?.files || uploadRes.data.files.length === 0) {
      throw new Error('图片上传失败，请重试')
    }
    return uploadRes.data.files.map((f: any) => f.url)
  }

  const handlePublish = async (content: string, images: string[], metadata: any) => {
    try {
      // 表单验证
      if (!metadata.category) {
        toast.error('请选择分类')
        return
      }

      if (content.trim().length < 10) {
        toast.error('内容至少10个字符')
        return
      }

      if (metadata.category === '交易' && !metadata.price) {
        toast.error('交易分类请填写价格')
        return
      }

      // 先上传图片（真实文件），用返回的 URL 提交帖子
      const imageUrls = await uploadImages(images)

      // 创建发布内容
      await createPost({
        userId: user.id,
        category: metadata.category,
        title: metadata.title || '',
        content: content.trim(),
        images: imageUrls,
        tags: metadata.tags || [],
        location: metadata.location || '',
        school: metadata.school || '',
        price: metadata.price,
        condition: metadata.condition,
        contactInfo: metadata.contactInfo,
        isAnonymous: metadata.isAnonymous || false,
        scheduledDate: metadata.isScheduled ? metadata.scheduledDate : null,
        scheduledTime: metadata.isScheduled ? metadata.scheduledTime : null
      })

      toast.success('发布成功！')
      navigate('/')
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('发布失败，请稍后重试')
      }
      // 向上抛出，让编辑器保留用户输入（不清空）
      throw error
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">发布动态</h1>
          <p className="text-sm text-gray-500 mt-1">分享你的校园生活，记录美好时光</p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
        >
          返回
        </Button>
      </div>

      {/* 增强编辑器 */}
      <EnhancedEditor
        onSave={handleSave}
        onPublish={handlePublish}
        className="w-full"
      />

      {/* 发布须知 */}
      <Card className="bg-amber-50 border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-amber-800">
            <AlertCircle className="w-5 h-5" />
            <span>发布须知</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-amber-800 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-2">内容规范</h4>
              <ul className="space-y-1 text-xs">
                <li>• 请遵守校园文明，不发布违法违规内容</li>
                <li>• 禁止发布色情、暴力、政治敏感内容</li>
                <li>• 不得恶意传播谣言或虚假信息</li>
                <li>• 尊重他人隐私，不得恶意传播他人信息</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">交易安全</h4>
              <ul className="space-y-1 text-xs">
                <li>• 二手交易请线下当面交易，注意安全</li>
                <li>• 失物招领请提供详细信息便于核实</li>
                <li>• 谨防诈骗，不要轻信陌生人转账要求</li>
                <li>• 如遇问题请及时联系管理员处理</li>
              </ul>
            </div>
          </div>
          <div className="mt-4 p-3 bg-amber-100 rounded-lg">
            <p className="text-xs font-medium">
              💡 温馨提示：发布内容将经过智能审核，违规内容将被自动拦截。如有疑问请联系客服QQ：3238467661
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}